import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  Feedback,
  FeedbackSubmission,
  FeedbackStats,
  FeedbackSummary,
  AppName
} from '../types/feedback';

export class FeedbackService {
  private static instance: FeedbackService;
  private cache = new Map<string, any>();

  static getInstance(): FeedbackService {
    if (!FeedbackService.instance) {
      FeedbackService.instance = new FeedbackService();
    }
    return FeedbackService.instance;
  }

  // Submit feedback using Firebase Function
  async submitFeedback(submission: FeedbackSubmission): Promise<{ success: boolean; feedbackId: string }> {
    try {
      const submitFeedbackFunction = httpsCallable(functions, 'submitFeedback');
      const result = await submitFeedbackFunction(submission);
      const data = result.data as { success: boolean; feedbackId: string; message: string };
      
      // Clear cache after successful submission
      this.clearCache();
      
      return {
        success: data.success,
        feedbackId: data.feedbackId
      };
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw new Error('Failed to submit feedback. Please try again.');
    }
  }

  // Send email notification using Firebase Function
  async sendEmailNotification(feedbackId: string): Promise<{ success: boolean; recipients: number }> {
    try {
      const sendEmailFunction = httpsCallable(functions, 'sendFeedbackEmailNotification');
      const result = await sendEmailFunction({ feedbackId });
      const data = result.data as { success: boolean; recipients: number; message: string };
      
      return {
        success: data.success,
        recipients: data.recipients
      };
    } catch (error) {
      console.error('Error sending email notification:', error);
      throw new Error('Failed to send email notification.');
    }
  }

  // Get all feedback from Firestore
  async getAllFeedback(): Promise<Feedback[]> {
    const cacheKey = 'allFeedback';
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 30000) { // 30 second cache
        return cached.data;
      }
    }

    try {
      // Import Firestore here to avoid circular dependency
      const { db } = await import('../config/firebase');
      const { collection, getDocs, query, orderBy, where } = await import('firebase/firestore');
      
      const feedbackRef = collection(db, 'feedback');
      
      // Try different query approaches to handle index issues
      let querySnapshot;
      try {
        // First try the indexed query
        const q = query(
          feedbackRef,
          where('isDeleted', '==', false),
          orderBy('timestamp', 'desc')
        );
        querySnapshot = await getDocs(q);
      } catch (indexError) {
        console.log('Indexed query failed, trying simple query:', indexError);
        try {
          // Fallback to simple query without compound index
          const simpleQuery = query(feedbackRef, orderBy('timestamp', 'desc'));
          const allDocs = await getDocs(simpleQuery);
          
          // Filter out deleted items manually
          const filteredDocs = {
            docs: allDocs.docs.filter(doc => {
              const data = doc.data();
              return !data.isDeleted;
            }),
            forEach: function(callback: any) {
              this.docs.forEach(callback);
            }
          };
          querySnapshot = filteredDocs as any;
        } catch (simpleError) {
          console.log('Simple query also failed, collection might be empty:', simpleError);
          this.cache.set(cacheKey, {
            data: [],
            timestamp: Date.now()
          });
          return [];
        }
      }
      
      const feedback: Feedback[] = [];
      
      querySnapshot.forEach((doc: any) => {
        const data = doc.data();
        feedback.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date()
        } as Feedback);
      });

      // Cache the result
      this.cache.set(cacheKey, {
        data: feedback,
        timestamp: Date.now()
      });

      return feedback;
    } catch (error) {
      console.error('Error getting all feedback:', error);
      throw new Error('Failed to load feedback data.');
    }
  }

  // Get feedback by app
  async getFeedbackByApp(appName: AppName): Promise<Feedback[]> {
    const cacheKey = `feedback_${appName}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 30000) { // 30 second cache
        return cached.data;
      }
    }

    try {
      const { db } = await import('../config/firebase');
      const { collection, getDocs, query, orderBy, where } = await import('firebase/firestore');
      
      const feedbackRef = collection(db, 'feedback');
      
      let querySnapshot;
      try {
        // Try indexed query first
        const q = query(
          feedbackRef,
          where('appName', '==', appName),
          where('isDeleted', '==', false),
          orderBy('timestamp', 'desc')
        );
        querySnapshot = await getDocs(q);
      } catch (indexError) {
        console.log(`Indexed query failed for ${appName}, trying simple approach:`, indexError);
        // Fallback: get all docs and filter manually
        const simpleQuery = query(feedbackRef, orderBy('timestamp', 'desc'));
        const allDocs = await getDocs(simpleQuery);
        
        const filteredDocs = {
          docs: allDocs.docs.filter(doc => {
            const data = doc.data();
            return data.appName === appName && !data.isDeleted;
          }),
          forEach: function(callback: any) {
            this.docs.forEach(callback);
          }
        };
        querySnapshot = filteredDocs as any;
      }
      const feedback: Feedback[] = [];
      
      querySnapshot.forEach((doc: any) => {
        const data = doc.data();
        feedback.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date()
        } as Feedback);
      });

      // Cache the result
      this.cache.set(cacheKey, {
        data: feedback,
        timestamp: Date.now()
      });

      return feedback;
    } catch (error) {
      console.error(`Error getting feedback for ${appName}:`, error);
      throw new Error(`Failed to load feedback for ${appName}.`);
    }
  }

  // Delete feedback
  async deleteFeedback(feedbackId: string): Promise<void> {
    try {
      const { db } = await import('../config/firebase');
      const { doc, updateDoc } = await import('firebase/firestore');
      
      const feedbackRef = doc(db, 'feedback', feedbackId);
      await updateDoc(feedbackRef, {
        isDeleted: true,
        deletedAt: new Date()
      });

      // Clear cache after deletion
      this.clearCache();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      throw new Error('Failed to delete feedback.');
    }
  }

  // Get feedback statistics
  async getFeedbackStats(): Promise<FeedbackStats> {
    const cacheKey = 'feedbackStats';
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 60000) { // 1 minute cache
        return cached.data;
      }
    }

    try {
      const allFeedback = await this.getAllFeedback();
      
      // Calculate app summaries
      const appSummaries: FeedbackSummary[] = [];
      const appNames: AppName[] = ['Employee Directory', 'Good Thing Tracker', 'Day Away Board', 'SOP Central'];
      
      for (const appName of appNames) {
        const appFeedback = allFeedback.filter(f => f.appName === appName);
        const latestFeedback = appFeedback.length > 0 ? appFeedback[0].timestamp : undefined;
        
        appSummaries.push({
          appName,
          count: appFeedback.length,
          latestFeedback
        });
      }

      // Get recent feedback (last 10)
      const recentFeedback = allFeedback.slice(0, 10);

      const stats: FeedbackStats = {
        totalFeedback: allFeedback.length,
        appSummaries,
        recentFeedback
      };

      // Cache the result
      this.cache.set(cacheKey, {
        data: stats,
        timestamp: Date.now()
      });

      return stats;
    } catch (error) {
      console.error('Error getting feedback stats:', error);
      throw new Error('Failed to load feedback statistics.');
    }
  }

  // Get feedback by ID
  async getFeedbackById(feedbackId: string): Promise<Feedback | null> {
    try {
      const { db } = await import('../config/firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      
      const feedbackRef = doc(db, 'feedback', feedbackId);
      const docSnap = await getDoc(feedbackRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date()
        } as Feedback;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting feedback by ID:', error);
      throw new Error('Failed to load feedback details.');
    }
  }

  // Clear cache
  clearCache(): void {
    this.cache.clear();
  }

  // Get paginated feedback for admin interface
  // NOTE: This legacy method uses client-side pagination. For large datasets, use getFeedbackPage.
  async getPaginatedFeedback(page: number = 1, limit: number = 10, appName?: AppName): Promise<{
    feedback: Feedback[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  }> {
    try {
      const allFeedback = appName ? await this.getFeedbackByApp(appName) : await this.getAllFeedback();
      
      const totalCount = allFeedback.length;
      const totalPages = Math.ceil(totalCount / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      
      const paginatedFeedback = allFeedback.slice(startIndex, endIndex);
      
      return {
        feedback: paginatedFeedback,
        totalCount,
        currentPage: page,
        totalPages,
        hasNext: page < totalPages,
        hasPrevious: page > 1
      };
    } catch (error) {
      console.error('Error getting paginated feedback:', error);
      throw new Error('Failed to load paginated feedback.');
    }
  }

  // Get paginated feedback page using Firestore cursors
  async getFeedbackPage(limitCount: number = 10, startAfterDoc?: any, appName?: AppName): Promise<{
    feedback: Feedback[];
    lastVisible: any;
  }> {
    try {
      const { db } = await import('../config/firebase');
      const { collection, getDocs, query, orderBy, where, limit, startAfter } = await import('firebase/firestore');

      const feedbackRef = collection(db, 'feedback');
      
      // Build constraints
      const constraints: any[] = [
        where('isDeleted', '==', false)
      ];

      if (appName) {
        constraints.push(where('appName', '==', appName));
      }

      // Add ordering
      constraints.push(orderBy('timestamp', 'desc'));

      // Add cursor if provided
      if (startAfterDoc) {
        constraints.push(startAfter(startAfterDoc));
      }

      // Add limit
      constraints.push(limit(limitCount));

      try {
        const q = query(feedbackRef, ...constraints);
        const querySnapshot = await getDocs(q);
        
        const feedback: Feedback[] = [];
        
        querySnapshot.forEach((doc: any) => {
          const data = doc.data();
          feedback.push({
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date()
          } as Feedback);
        });

        return {
          feedback,
          lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null
        };
      } catch (indexError) {
        console.log('Indexed pagination query failed, trying fallback:', indexError);
        // Fallback to client-side slicing if index is missing
        // This maintains compatibility but warns about performance
        const allFeedback = appName ? await this.getFeedbackByApp(appName) : await this.getAllFeedback();
        
        // Find start index based on startAfterDoc if possible, otherwise crude approximation
        let startIndex = 0;
        if (startAfterDoc) {
           const startId = startAfterDoc.id;
           const index = allFeedback.findIndex(f => f.id === startId);
           if (index !== -1) {
             startIndex = index + 1;
           }
        }
        
        const sliced = allFeedback.slice(startIndex, startIndex + limitCount);
        return {
          feedback: sliced,
          lastVisible: sliced.length > 0 ? { id: sliced[sliced.length - 1].id } : null // Mock doc snapshot
        };
      }
    } catch (error) {
      console.error('Error getting feedback page:', error);
      throw new Error('Failed to load feedback page.');
    }
  }

  // Get total count of feedback
  async getFeedbackCount(appName?: AppName): Promise<number> {
    try {
      const { db } = await import('../config/firebase');
      // Use dynamic import for getCountFromServer if available in your firebase SDK version
      // checking if it exports it, otherwise fallback
      const firestoreModule = await import('firebase/firestore');
      
      // @ts-ignore - getCountFromServer might not be in the type definition if older version
      if (firestoreModule.getCountFromServer) {
         const { collection, query, where, getCountFromServer } = firestoreModule;
         const feedbackRef = collection(db, 'feedback');
         const constraints = [where('isDeleted', '==', false)];
         
         if (appName) {
           constraints.push(where('appName', '==', appName));
         }
         
         const q = query(feedbackRef, ...constraints);
         const snapshot = await getCountFromServer(q);
         return snapshot.data().count;
      }
      
      // Fallback
      const all = appName ? await this.getFeedbackByApp(appName) : await this.getAllFeedback();
      return all.length;
    } catch (e) {
      console.error("Count aggregation failed, falling back to array length", e);
      const all = appName ? await this.getFeedbackByApp(appName) : await this.getAllFeedback();
      return all.length;
    }
  }

  // Debug function to test Firebase function database access
  async debugUserAppAccess(): Promise<any> {
    try {
      const debugFunction = httpsCallable(functions, 'debugUserAppAccess');
      const result = await debugFunction();
      console.log('Debug function result:', result.data);
      return result.data;
    } catch (error) {
      console.error('Error calling debug function:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const feedbackService = FeedbackService.getInstance();

// Named exports for convenience
export const {
  submitFeedback,
  getAllFeedback,
  getFeedbackByApp,
  deleteFeedback,
  getFeedbackStats,
  getFeedbackById,
  getPaginatedFeedback,
  getFeedbackPage,
  getFeedbackCount,
  sendEmailNotification,
  debugUserAppAccess
} = feedbackService;